// Report-param grammar for a matrix sub-item block -- one batch of a matrix, rapid-association or
// scale question drawn as a question of its own.
//
// Four params carry the block's state, addressed by prefix:
//   cm   the chart's mode -- or the marker saying the word cloud is showing instead of a chart
//   z    the chart's zoom
//   s    the sort order
//   smc  the summary metrics beside the answers, and whether the answer columns are drawn
//
// `cm` is overloaded on purpose, the way the coffee views left it: a batch showing its answer
// variants as a word cloud has no chart mode to remember, so the one param says which of the two is
// on screen. `off` is the marker for a batch that offers the cloud by default and was turned back.
//
// The grammar was split across three coffee views -- the block owned the cloud marker, the chart
// view the mode and the zoom, the items list the sort order. They are one bag here.

import { isRType as calcIsRType } from '@aytm/app/stats/page/components/lib/utils'
import { DEFAULT_SORT_ORDER, resolveSortOrder } from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'
export { calcSortParamsSection, resolveSortOrder } from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import {
  WC_CHART_MODE,
  PERCENT_CHART_MODES,
  isWcChartMode,
  calcQuestionChartMode
} from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { parseRowSelection } from '@aytm/app/stats/page/question-views/mx/lib/summary/params'

export { DEFAULT_SORT_ORDER }

export { WC_CHART_MODE, isWcChartMode }

const PIE_CHART_MODE = 'pie'

// A batch that offers the cloud by default shows it until the researcher says otherwise, and `off`
// is how saying otherwise is written down. The default is settled here, as the block opens, so that
// from then on the mode says outright which of the two is up.
export const resolveChartMode = ({ cm } = {}, format = {}) => {
  if (isWcChartMode(cm)) return WC_CHART_MODE
  if (null == cm && isWcChartMode(format?.chartMode)) return WC_CHART_MODE

  return cm || null
}

export const isWordCloud = chartMode => isWcChartMode(chartMode)

// The chart mode the batch itself declares, which the chart falls back to before the report's. The
// cloud marker is not one: a batch declaring it has no chart mode of its own to fall back to.
export const calcModelChartMode = (chartMode, format = {}) => {
  if (isWcChartMode(chartMode)) return WC_CHART_MODE
  if (isWcChartMode(format?.chartMode)) return null

  return format?.chartMode || null
}

// A batch counting respondents has no pie to slice, and one told to show percents only shows them
// whatever was picked.
export const calcDefaultChartMode = ({
  chartMode,
  questionChartMode,
  globalChartMode,
  format,
  type,
  variant,
  questionType,
  questionSubType,
  subType
} = {}) => {
  let mode = calcQuestionChartMode(chartMode, questionChartMode, globalChartMode)

  if (calcIsRType({ type, variant, questionType, questionSubType, subType }) && PIE_CHART_MODE === mode) {
    mode = 'respondents'
  }

  if (true === format?.percentsOnly && !PERCENT_CHART_MODES.includes(mode)) return 'percents'

  return mode
}

// A chart mode that says what the batch already says is not worth storing -- except when the batch
// says "cloud" and the researcher wants a chart, which is what there is no silence for. A pie has
// no axis to zoom, so picking one drops the zoom with it.
export const calcChartParamsSection = ({ chartMode, format, ...question } = {}) => {
  const stored = format?.chartMode || 'off'

  if (isWcChartMode(stored) && !isWcChartMode(chartMode)) return { cm: 'off' }

  const section = { cm: chartMode === stored ? null : chartMode || null }

  const resolved = calcDefaultChartMode({ ...question, chartMode, format })

  return PIE_CHART_MODE === resolved ? { ...section, z: null } : section
}

// The state a block opens in, read off the params bag it is handed: every key by its own rule.
export const resolveReportParams = ({ params = {}, format, defaultSortOrder, metrics } = {}) => ({
  chartMode: resolveChartMode(params, format),
  zoomLevel: decodeZoomParam(params.z),
  sortOrder: resolveSortOrder(params.s, defaultSortOrder),
  // a property of the sub-question's summary, not of the chart mode, so a mode change leaves it alone
  metricSelection: parseRowSelection(params.smc, metrics)
})
